/**
 * THE GLIDE — mortal motion made continuous (2026-08-23, Griffin: "smooth
 * and realtime").
 *
 * The sim moves a party once per world-HOUR — ~104 m a stride at walking
 * pace, which at 192× is a visible teleport every nineteen real seconds.
 * The renderer is handed a DISPLAYED position instead: each frame it closes
 * `dtWorld / GLIDE_TAU` of the gap to the sim's true position. Exponential
 * approach with a two-world-hour time constant means the displayed speed
 * self-matches the sim's own average — walking, fleeing, path bonus and all,
 * with the view never knowing the mode — the figure glides a couple of
 * world-minutes behind the truth, and it converges whenever the truth stands
 * still. No overshoot, no oscillation, no stored route.
 *
 * Pure view state: the sim is never read ahead and never written (law 9).
 * A gap past GLIDE_SNAP is not a walk — a fresh muster, a migration landing,
 * a restore — and snaps, because gliding across a teleport would draw a
 * journey nobody took.
 */
export interface GlideState {
  x: number;
  z: number;
  /** Unit heading of the last real motion — held while standing. */
  hx: number;
  hz: number;
}

/** Time constant of the approach, in world-seconds (two world-hours). */
export const GLIDE_TAU = 2 * 3600;
/** Gaps past this are relocations, not walks: snap. */
export const GLIDE_SNAP = 500;

/** One frame of glide toward the sim's true (sx, sz). Mutates `g`. */
export function glideStep(
  g: GlideState, sx: number, sz: number, dtWorld: number,
): void {
  const dx = sx - g.x;
  const dz = sz - g.z;
  const gap = Math.hypot(dx, dz);
  if (gap > GLIDE_SNAP) {
    g.x = sx;
    g.z = sz;
    return;
  }
  if (gap < 0.01 || dtWorld <= 0) return;
  const k = Math.min(1, dtWorld / GLIDE_TAU);
  g.x += dx * k;
  g.z += dz * k;
  g.hx = dx / gap;
  g.hz = dz / gap;
}
